import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { format } from 'date-fns';
import { ArrowLeftIcon } from '@heroicons/react/24/outline';
import { resourceOrderAPI } from '../../../services/resourceOrderApi';
import { ResourceOrder } from '../../../types/resourceOrder';
import { ORDER_RESOURCES } from '../../../content/orderResources';
import LoadingSkeleton from '../../../components/ui/LoadingSkeleton';
import ErrorState from '../../../components/ui/ErrorState';
import Breadcrumb from '../../../components/layout/Breadcrumb';
import { usePageTitle } from '../../../hooks/usePageTitle';

export default function OrderDetail() {
  const { id } = useParams<{ id: string }>();
  const [order, setOrder] = useState<ResourceOrder | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  usePageTitle(order ? `${order.firstName} ${order.lastName}` : 'Resource order');

  useEffect(() => {
    if (!id) {
      setError(new Error('Invalid order'));
      setLoading(false);
      return;
    }
    resourceOrderAPI
      .fetchOrder(id)
      .then(setOrder)
      .catch((err) => setError(err instanceof Error ? err : new Error('Failed to load order')))
      .finally(() => setLoading(false));
  }, [id]);

  if (loading) {
    return (
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <LoadingSkeleton lines={10} />
      </div>
    );
  }

  if (error || !order) {
    return (
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <ErrorState error={error || new Error('Order not found')} title="Order not found" onRetry={() => window.location.reload()} />
      </div>
    );
  }

  const address = [order.streetAddress, order.streetAddress2, `${order.city}, ${order.province} ${order.postalCode}`]
    .filter(Boolean)
    .join('\n');

  return (
    <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <Breadcrumb
        items={[
          { label: 'Home', to: '/' },
          { label: 'Resource orders', to: '/admin/orders' },
          { label: `${order.firstName} ${order.lastName}` },
        ]}
      />
      <Link to="/admin/orders" className="inline-flex items-center gap-2 text-primary hover:text-primary-dark mb-6">
        <ArrowLeftIcon className="h-5 w-5" />
        <span>Back to orders</span>
      </Link>
      <h1 className="text-3xl font-bold text-gray-900 mb-2">
        {order.firstName} {order.lastName}
      </h1>
      <p className="text-gray-500 mb-8">{format(new Date(order.createdAt), 'MMMM d, yyyy h:mm a')}</p>

      <dl className="space-y-4 text-gray-700">
        <div>
          <dt className="text-sm font-medium text-gray-500">Email</dt>
          <dd>{order.email}</dd>
        </div>
        <div>
          <dt className="text-sm font-medium text-gray-500">Organization</dt>
          <dd>
            {order.organizationName}
            <span className="block text-sm text-gray-500">{order.instituteType}</span>
          </dd>
        </div>
        <div>
          <dt className="text-sm font-medium text-gray-500">Delivery</dt>
          <dd className="whitespace-pre-line">{address}</dd>
        </div>
        {order.deliveryInstructions && (
          <div>
            <dt className="text-sm font-medium text-gray-500">Special delivery instructions</dt>
            <dd className="whitespace-pre-line">{order.deliveryInstructions}</dd>
          </div>
        )}
      </dl>

      <h2 className="text-xl font-semibold text-gray-900 mt-8 mb-4">Copies</h2>
      <ul className="space-y-3">
        {ORDER_RESOURCES.map((resource) => (
          <li key={resource.id} className="flex items-baseline justify-between gap-4 border-b border-gray-100 pb-3">
            <span className="text-gray-700">{resource.title}</span>
            <span className="font-medium text-gray-900">{order.quantities[resource.id] ?? 0}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}
